import { HttpError } from '@euler/lib/request/HttpError';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';

export type BiOfferedDatasetsState =
  | { kind: 'loading' }
  | { kind: 'ready'; datasets: BiUploadedDatasetView[] }
  | { kind: 'failed' };

/**
 * The datasets a choice offers, read one by one: one the caller can no
 * longer use reads as missing and is left out.
 */
export function useBiOfferedDatasets(
  datasetIds: string[],
): BiOfferedDatasetsState {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiOfferedDatasetsState>({
    kind: 'loading',
  });
  const key = datasetIds.join(',');
  useEffect(() => {
    let active = true;
    const ids = key ? key.split(',') : [];
    Promise.all(
      ids.map(id =>
        biService.getDataset(id).then(
          detail =>
            detail.kind === BiDatasetRefKind.Upload ? detail.dataset : null,
          (error: unknown) => {
            if (
              error instanceof HttpError &&
              error.code === 'resource_not_found'
            )
              return null;
            throw error;
          },
        ),
      ),
    )
      .then(found => {
        if (active)
          setState({
            kind: 'ready',
            datasets: found.filter(
              (dataset): dataset is BiUploadedDatasetView => !!dataset,
            ),
          });
      })
      .catch(error => {
        console.error('BI offered datasets failed', error);
        if (active) setState({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [biService, key]);
  return state;
}
